// **The demo stage host** (`demo-presentation-shell` tasks 6.6 and 7.2/7.4, design **DD4**/**DD5**).
//
// The canvas host for the interactive world: it owns the renderer's lifetime, the latest-snapshot
// -wins render loop, the selection, and the pointer chapter's two gestures. It owns **no**
// simulation — every snapshot it draws arrived from the worker (**DD3**), and every command it
// resolves leaves through the injected `submitCommand` seam.
//
// **The renderer factory is an injected seam with no default** — the `AmbientBackground` /
// `ReviewFrame` precedent. A real `CanvasRenderer` wants a GPU context, so a bare mount and every
// jsdom test render inertly rather than constructing Pixi.
//
// **The render loop consumes the LATEST snapshot, and drops the superseded ones.** A queue would
// convert one slow frame into growing latency, which on stage reads as the game ignoring the
// presenter's clicks. A frame with nothing new draws nothing at all.
//
// **The selection pulse is the RENDERER's, not a DOM overlay** (2026-08-12). It was a DOM div —
// two rings, paper then accent, from `stage-hud-states.html` panel A — chosen to keep
// `CanvasRenderer` unmodified (the **D-156** argument). That treatment was replaced on request with
// a same-size bright-blue foreground copy *in the bee's own silhouette*, and a div cannot be a
// silhouette: it can only ever be a circle or a box around one. Selection now rides
// `RenderInput.selectedBeeId`; the renderer pulses that exact frame above the ordinary body and
// below damage flash, giving Colony, Sandbox and graded Review the same treatment.
//
// One consequence worth knowing: the selection needs no per-frame placement any more (it rides the
// bee's sprite node), but a *click alone* must still force a redraw, so the draw loop tracks
// `lastDrawnSelection` beside `lastDrawnCamera`.
//
// **The world is never React state.** A world in state would re-render this whole subtree at the
// draw rate; `practice-worker.ts`'s `DEFAULT_PROGRESS_MIN_INTERVAL_MS` records what that measured
// like. The selected bee's *id* is state (it changes on a click, not on a frame), and the draw loop
// reads it through a ref.

import { useEffect, useMemo, useRef, useState, type CSSProperties, type ReactElement } from 'react';
import {
  performanceClock,
  rafFrameScheduler,
  type Clock,
  type FrameScheduler,
} from '../../canvas/frame-scheduler';
import { PICK_RADIUS_PX, pickEntityAt } from '../../canvas/pick';
import {
  Y_SQUASH,
  hiveCloseCamera,
  screenToWorld,
  type Camera,
} from '../../canvas/projection';
import type { RenderInput } from '../../canvas/render-input';
import { panByScreenDelta, zoomAboutScreenPoint } from '../../ui/camera/camera-gestures';
import { useCanvasCameraGestures } from '../../ui/camera/use-canvas-camera-gestures';
import type { BeeRecord } from '../../net/wire/messages';
import type { AmbientRenderer } from '../../ui/home/AmbientBackground';
import { DEMO_TICK_HZ } from './stage-clock';
import { DEFAULT_PATCH_RADIUS_U } from './stage-frame';
import { adaptDemoSnapshot } from './stage-snapshot';
import type { DemoBeeCommandOp } from './stage-driver';
import type { StageFrame } from './stage-frame';

/**
 * The colony's hive radius in world units, used to decide whether a right-click landed on the
 * hive. 32 u is the 30-cell starting hive's radius (DESIGN §14's hive-work milling row states it),
 * and the stage's colonies start at the standard composition. Overridable so the composition root
 * can pass the world's own derived value once C-1 reports one.
 */
export const DEMO_HIVE_RADIUS_U = 32;

// The world-space fallback radius for a patch whose snapshot carries none — DESIGN §14's seed patch
// radius — is `DEFAULT_PATCH_RADIUS_U`, imported at the top from `stage-frame.ts`, which pads the
// stage frame by it. C-1 carries a per-patch `radius` and that is preferred; the fallback only
// covers a snapshot that omits one.

export interface DemoStageProps {
  /** No default (see the header). */
  createRenderer?: (container: HTMLElement, tickRateHz: number) => AmbientRenderer;
  /** Subscribes to the worker's render snapshots (C-1's own JSON). Returns an unsubscribe. */
  subscribe?: (listener: (json: string) => void) => () => void;
  /** Where a resolved pointer command goes — the worker host's `submitCommand` in production. */
  submitCommand?: (op: DemoBeeCommandOp) => void;
  ownColonyId: number;
  /**
   * The stage camera. Defaults to one centred on {@link DemoStageProps.ownHive} — see
   * {@link defaultStageCamera}.
   */
  camera?: Camera;
  /**
   * The presenter colony's hive point in the world the cursor's chapter staged. The default
   * camera's *fallback* frame — used only when no {@link DemoStageProps.stageFrame} is given.
   * Supplied by `DemoApp` from the cue list's own `configure` anchor, so it is a fact about the
   * staged chapter rather than a constant restated here.
   */
  ownHive?: readonly [number, number];
  /**
   * **What the default camera frames** (playthrough finding, 2026-08-09): the world rectangle the
   * cursor's chapter actually staged, from `DemoApp`'s {@link stageFrameOf} over the same
   * `configure` anchor {@link DemoStageProps.ownHive} comes from. Given one, the camera's scale is
   * *derived* from the content instead of being the 1.0 display constant `hiveCloseCamera` carries
   * — which showed ~1920 × 1500 world units on a 1080p projector and rendered the whole of chapter
   * 1 (everything inside ±135 u) as a thumbnail.
   */
  stageFrame?: StageFrame;
  /** The pixel viewport the default camera frames the hive inside. Defaults to the window's, which
   * is what the surface fills — `/demo` is a full-screen projector surface, `inset: 0` all the way
   * down. */
  viewport?: { width: number; height: number };
  /**
   * **What the default camera centres on instead of the frame's centre** (2026-08-09 request):
   * chapter 1 opens on its one staged bee — offset from the hive, zoomed so the hive is
   * off-screen — rather than on the whole staged rectangle. The scale is still derived from
   * {@link DemoStageProps.stageFrame}; only the centre moves. Omitted, the frame's centre wins.
   */
  focus?: readonly [number, number];
  hiveRadiusU?: number;
  tickRateHz?: number;
  /** Mutable presentation multiplier; never changes the simulation tick-rate provenance. */
  playbackRate?: number;
  frameScheduler?: FrameScheduler;
  clock?: Clock;
}

/** What the pointer needs from a snapshot that the renderer's own input types do not carry: a
 * patch's world radius and workability and the hive points, all read from C-1 directly. */
interface HitTargets {
  patches: { id: number; position: [number, number]; radius: number; workable: boolean }[];
  hive: [number, number] | null;
}

function hitTargetsOf(parsed: unknown, ownColonyId: number): HitTargets {
  const source = (parsed ?? {}) as {
    patches?: { id: number; x: number; y: number; radius?: number; pool?: number; recovering?: boolean }[];
    colonies?: { id: number; hive: { x: number; y: number } }[];
  };
  const own = (source.colonies ?? []).find((colony) => colony.id === ownColonyId);
  return {
    patches: (source.patches ?? []).map((patch) => ({
      id: patch.id,
      position: [patch.x, patch.y],
      radius: patch.radius ?? DEFAULT_PATCH_RADIUS_U,
      // The same live-flower predicate `adaptDemoSnapshot` renders by. C-1 refuses
      // `collect_nectar` at a drained or recovering patch as `unknown_patch` — silently, from the
      // audience's seat (playthrough finding, 2026-08-09: "right-click does nothing") — so an
      // unworkable flower must not be offered as a collect target at all; the click falls through
      // to `move_to`, and the bee at least walks to where the presenter pointed.
      workable: (patch.pool ?? 0) > 0 && patch.recovering !== true,
    })),
    hive: own === undefined ? null : [own.hive.x, own.hive.y],
  };
}

// `touchAction: 'none'` so a touch drag reaches the pan gesture rather than scrolling the page.
const SURFACE_STYLE: CSSProperties = {
  position: 'absolute',
  inset: 0,
  overflow: 'hidden',
  touchAction: 'none',
};

// `StageFrame`/`stageFrameOf` moved to `stage-frame.ts` (presentation-polish Wave 2) once
// `chapters.ts` needed the same rectangle to place the raid's entry point outside it — see that
// file's header. Re-exported here so every existing importer of this module is unchanged.
export { stageFrameOf } from './stage-frame';
export type { StageFrame } from './stage-frame';

/**
 * **The camera the stage opens on** (E2E finding B2, then the playthrough finding of 2026-08-09).
 *
 * There was no default at all: `{offsetX: 0, offsetY: 0, scale: 1}` maps world (0, 0) to *screen*
 * (0, 0), so a hive at the world origin — which is where every chapter stages it — rendered
 * three-quarters off the top-left corner. `hiveCloseCamera` fixed the aim; it did not fix the
 * **zoom**, because its `COLONY_CLOSE_SCALE` is a phone-sized display constant (1.0 ⇒ ~1920 × 1500
 * world units on a projector), and a chapter that stages everything inside ±135 u played as a
 * thumbnail in the middle of an empty field.
 *
 * So with a `frame` the scale is *derived*: the largest that still fits the frame's half-extents in
 * both axes (y through the ×0.72 squash, exactly as `worldToScreen` applies it), with the offsets
 * that put the frame's centre at the viewport's centre. Without one, the old hive-only behaviour is
 * unchanged — a caller with nothing staged to frame is no worse off than before.
 */
export function defaultStageCamera(
  ownHive: readonly [number, number] | undefined,
  viewport: { width: number; height: number } | undefined,
  frame?: StageFrame,
  focus?: readonly [number, number],
): Camera {
  const port = viewport ?? windowViewport();
  if (frame === undefined) {
    return hiveCloseCamera(focus ?? ownHive ?? [0, 0], port);
  }
  const scale = Math.min(port.width / 2 / frame.halfX, port.height / 2 / (frame.halfY * Y_SQUASH));
  if (!Number.isFinite(scale) || scale <= 0) {
    // A zero-sized (or not-yet-laid-out) viewport. The fallback is a real camera rather than a NaN
    // one — the same total-function posture `hiveCloseCamera` keeps for a null hive.
    return hiveCloseCamera(focus ?? frame.centre, port);
  }
  const [cx, cy] = focus ?? frame.centre;
  return {
    offsetX: cx - port.width / 2 / scale,
    offsetY: cy - port.height / 2 / (scale * Y_SQUASH),
    scale,
  };
}

/** The wheel-zoom clamp. The demo's frame-derived scales sit around 5–6; 0.5 shows the whole
 * staged world with room to spare, 30 fills the projector with a single bee. */
const DEMO_ZOOM_BOUNDS = { minScale: 0.5, maxScale: 30 };

function windowViewport(): { width: number; height: number } {
  return {
    width: typeof window === 'undefined' ? 0 : window.innerWidth,
    height: typeof window === 'undefined' ? 0 : window.innerHeight,
  };
}

export function DemoStage({
  createRenderer,
  subscribe,
  submitCommand,
  ownColonyId,
  camera,
  ownHive,
  stageFrame,
  viewport,
  focus,
  hiveRadiusU = DEMO_HIVE_RADIUS_U,
  tickRateHz = DEMO_TICK_HZ,
  playbackRate = 1,
  frameScheduler,
  clock,
}: DemoStageProps): ReactElement {
  const hostRef = useRef<HTMLDivElement | null>(null);
  const surfaceRef = useRef<HTMLDivElement | null>(null);
  const [renderer, setRenderer] = useState<AmbientRenderer | null>(null);
  const rendererRateRef = useRef<number | null>(null);
  const [selectedId, setSelectedId] = useState<number | null>(null);

  /** The newest snapshot, and whether it has been drawn. A *slot*, never a queue. */
  const pendingRef = useRef<string | null>(null);
  /** The world last drawn, for picking and for the outline. */
  const worldRef = useRef<ReturnType<typeof adaptDemoSnapshot>['world'] | null>(null);
  /** The last adapted snapshot, so a camera move can redraw the world it already has rather than
   * waiting up to a tick for the next snapshot to arrive. */
  const adaptedRef = useRef<ReturnType<typeof adaptDemoSnapshot> | null>(null);
  const targetsRef = useRef<HitTargets>({ patches: [], hive: null });
  const selectedRef = useRef<number | null>(null);
  selectedRef.current = selectedId;

  /** The presenter's own pan/zoom, once they have touched the camera (2026-08-09 request:
   * scrolling zooms, dragging pans). `null` — the cue-derived default camera below. Cleared when
   * the default's identity changes (a chapter jump, a reconfigure), so each chapter still opens
   * on its own framing. */
  const [userCamera, setUserCamera] = useState<Camera | null>(null);

  const hiveX = ownHive?.[0];
  const hiveY = ownHive?.[1];
  const frameX = stageFrame?.centre[0];
  const frameY = stageFrame?.centre[1];
  const frameHalfX = stageFrame?.halfX;
  const frameHalfY = stageFrame?.halfY;
  const focusX = focus?.[0];
  const focusY = focus?.[1];
  useEffect(() => {
    setUserCamera(null);
    // By value, for the same reason as the memo below.
  }, [hiveX, hiveY, frameX, frameY, frameHalfX, frameHalfY, focusX, focusY]);
  const activeCamera = useMemo<Camera>(
    () =>
      camera ??
      userCamera ??
      defaultStageCamera(
        hiveX === undefined || hiveY === undefined ? undefined : [hiveX, hiveY],
        viewport,
        frameX === undefined || frameY === undefined || frameHalfX === undefined || frameHalfY === undefined
          ? undefined
          : { centre: [frameX, frameY], halfX: frameHalfX, halfY: frameHalfY },
        focusX === undefined || focusY === undefined ? undefined : [focusX, focusY],
      ),
    // Depended on by value, not by array identity: `DemoApp` derives `ownHive` and `stageFrame`
    // from the cue list on every render, so a tuple/object dependency would rebuild the camera
    // (and restart the draw loop) every frame.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [camera, userCamera, hiveX, hiveY, frameX, frameY, frameHalfX, frameHalfY, focusX, focusY, viewport?.width, viewport?.height],
  );
  /** The camera the draw loop and the pointer handlers actually read — a ref, so a gesture frame
   * never restarts the draw-loop effect. */
  const cameraRef = useRef<Camera>(activeCamera);
  cameraRef.current = activeCamera;

  // The renderer's lifetime, keyed on the factory **and the fixed tick rate** so nothing else about
  // playback tears Pixi down.
  //
  // `tickRateHz` is in the key deliberately, and it is the only thing besides the factory that is:
  // `PresentationClock`, `CorpseTracker` and `DamageFlashTracker` all take the rate as a
  // constructor argument and hold it `readonly`, so a rate that changes cannot be pushed into a
  // live renderer — the same constructor-only constraint `InMatchCanvas` documents for the wire
  // rate, and it resolves the same way, with one rebuild per transition. The demo's rate only
  // changes when the presenter presses `s`, and the 2x default is already correct at construction,
  // so the common case is zero rebuilds and the worst case is one per deliberate keypress.
  useEffect(() => {
    const host = hostRef.current;
    if (host === null || createRenderer === undefined) {
      return undefined;
    }
    let built: AmbientRenderer;
    try {
      built = createRenderer(host, tickRateHz);
      built.setPresentationRate?.(playbackRate);
      rendererRateRef.current = playbackRate;
    } catch (error) {
      // A factory that throws is the same outcome as a bare mount: no picture, nothing said to
      // the audience. The developer gets the console.
      console.error('waggle: the demo stage renderer could not be constructed', error);
      return undefined;
    }
    // D-290, and this surface has the least slack of the five hosts: the stage is driven live in
    // front of an audience, so a leaked WebGL context is a context the presenter's device is not
    // spending on the picture. Like `AmbientBackground`, its cleanup destroys through a state
    // setter that reads `null` until `init()` resolves, so a run cancelled mid-construction was
    // reclaimed by nothing until its own `.then()` fired — and by nothing ever on a hung load.
    const controller = new AbortController();
    void built.init(controller.signal).then(
      () => {
        if (controller.signal.aborted) {
          // Guarded, and with no `.catch` behind this handler an exception would otherwise be an
          // unhandled rejection. See `AmbientBackground`'s twin of this block.
          try {
            built.destroy();
          } catch (teardownError: unknown) {
            console.error('waggle: the demo stage renderer failed to tear down', teardownError);
          }
          return;
        }
        setRenderer(built);
      },
      // Two-argument `then`, not a trailing `.catch`: a chained catch would also report a
      // teardown fault from the handler above as a start-up failure. Only an `init()` rejection
      // is an init failure.
      (error: unknown) => {
        console.error('waggle: the demo stage renderer failed to start', error);
      },
    );
    return () => {
      // The controller replaced this run's `disposed` flag outright: it answers the same
      // question, cannot be reset by a later run, and is the only signal that reaches a
      // construction still in flight.
      controller.abort();
      setRenderer((current) => {
        current?.destroy();
        rendererRateRef.current = null;
        return null;
      });
    };
    // The seams are DI, injected once; `tickRateHz` is the one reactive input (see above).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [createRenderer, tickRateHz]);

  useEffect(() => {
    if (renderer !== null && rendererRateRef.current !== playbackRate) {
      renderer.setPresentationRate?.(playbackRate);
      rendererRateRef.current = playbackRate;
    }
  }, [playbackRate, renderer]);

  // The snapshot subscription. Independent of the renderer: snapshots keep arriving (and keep
  // superseding one another) whether or not there is anything to draw them with.
  useEffect(() => {
    if (subscribe === undefined) {
      return undefined;
    }
    return subscribe((json) => {
      pendingRef.current = json;
    });
  }, [subscribe]);

  // The draw loop. Latest wins; a frame with nothing new — no snapshot AND no camera move —
  // draws nothing. The camera comes from `cameraRef` rather than the effect closure, so a
  // gesture's per-frame camera state never tears the loop down.
  useEffect(() => {
    if (renderer === null) {
      return undefined;
    }
    const frames = frameScheduler ?? rafFrameScheduler;
    const now = clock ?? performanceClock;
    let stopped = false;
    let handle: number | null = null;
    let lastAt = now.now();
    let lastDrawnCamera: Camera | null = null;
    /** The selection the last drawn frame carried, so a click alone forces a redraw. */
    let lastDrawnSelection: number | null = null;

    /** The frame's render input with the current selection attached — the renderer draws the blue
     * foreground silhouette pulse from this (`RenderInput.selectedBeeId`). Read from the ref rather than from
     * React state for the same reason the world is: this runs in the draw loop, not in a render. */
    const withSelection = (base: RenderInput): RenderInput =>
      selectedRef.current === null ? base : { ...base, selectedBeeId: selectedRef.current };

    const draw = (): void => {
      const cam = cameraRef.current;
      const json = pendingRef.current;
      if (json === null) {
        // No new world, but the presenter may have moved the camera OR clicked a different bee —
        // redraw the world already adapted so a pan/zoom gesture and a selection change both land
        // on this frame instead of waiting for the next tick.
        const selectionMoved = lastDrawnSelection !== selectedRef.current;
        if (adaptedRef.current !== null && (lastDrawnCamera !== cam || selectionMoved)) {
          const at = now.now();
          const elapsedMs = at - lastAt;
          lastAt = at;
          renderer.render(
            adaptedRef.current.world,
            cam,
            elapsedMs,
            false,
            withSelection(adaptedRef.current.renderInput),
          );
          lastDrawnCamera = cam;
          lastDrawnSelection = selectedRef.current;
          dropSelectionIfGone();
        }
        return;
      }
      pendingRef.current = null;
      let parsed: unknown;
      try {
        parsed = JSON.parse(json);
      } catch {
        console.error('waggle: the demo stage was posted a snapshot that is not JSON');
        return;
      }
      // A refusal or an absent field is loud (`stage-snapshot.ts`), and it must not take the show
      // down with it: the last good world stays on screen and the developer gets the message.
      let adapted: ReturnType<typeof adaptDemoSnapshot>;
      try {
        adapted = adaptDemoSnapshot(parsed, { ownColonyId });
      } catch (error) {
        console.error('waggle: the demo stage refused a snapshot', error);
        return;
      }
      worldRef.current = adapted.world;
      adaptedRef.current = adapted;
      targetsRef.current = hitTargetsOf(parsed, ownColonyId);
      const at = now.now();
      const elapsedMs = at - lastAt;
      lastAt = at;
      renderer.render(adapted.world, cam, elapsedMs, false, withSelection(adapted.renderInput));
      lastDrawnCamera = cam;
      lastDrawnSelection = selectedRef.current;
      dropSelectionIfGone();
    };

    // What the old DOM-outline positioner did BESIDES positioning, which is the part that was
    // behaviour rather than decoration: a selection whose bee is gone from the world is dropped.
    // The pulse itself is now drawn by the renderer from `renderInput.selectedBeeId`, which needs
    // no per-frame placement — it rides the bee's own sprite node.
    const dropSelectionIfGone = (): void => {
      const id = selectedRef.current;
      if (id === null) return;
      if (worldRef.current?.bees.get(id) === undefined) {
        setSelectedId(null);
      }
    };

    const tick = (): void => {
      if (stopped) return;
      draw();
      handle = frames.schedule(tick);
    };
    handle = frames.schedule(tick);
    return () => {
      stopped = true;
      if (handle !== null) frames.cancel(handle);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [renderer, ownColonyId]);

  /**
   * **What the presenter is allowed to point at** (playthrough finding, 2026-08-09): her own
   * colony's bees that carry a type.
   *
   * Two bees were pickable that must never be. The **queen** stands on the hive point at the middle
   * of the shot, so chapter 1 offered two selectable bees where the script narrates one — and with
   * her selected, a right-click submitted her id, which `waggle-round`'s `submit_command` refuses
   * as `unknown_bee` for the **whole batch** (she carries no `type_id`), silently, so the beat read
   * as "right-click does nothing". An **enemy** raider is the same class of mistake with an
   * authorization flavour: it is not the presenter's bee to command.
   *
   * Filtering at *pick* time rather than at submit time is what makes the projector honest — the
   * queen and the raider stay rendered, they simply never take an outline. The submit path checks
   * the same predicate because a selection can go stale between the click and the right-click (the
   * bee died, or the world was reconfigured under it).
   */
  const isCommandable = (bee: BeeRecord | undefined): bee is BeeRecord =>
    bee !== undefined && bee.colonyId === ownColonyId && bee.typeId !== null;

  /** The pointer's world point, from the surface's own coordinate space. Reads `cameraRef` (not
   * the render-time camera) so a click mid-gesture resolves against the camera actually drawn. */
  const worldPointOf = (event: { clientX: number; clientY: number }): [number, number] => {
    const rect = surfaceRef.current?.getBoundingClientRect();
    return screenToWorld(cameraRef.current, {
      x: event.clientX - (rect?.left ?? 0),
      y: event.clientY - (rect?.top ?? 0),
    });
  };

  const screenPointOf = (event: { clientX: number; clientY: number }): { x: number; y: number } => {
    const rect = surfaceRef.current?.getBoundingClientRect();
    return { x: event.clientX - (rect?.left ?? 0), y: event.clientY - (rect?.top ?? 0) };
  };

  /**
   * **Pan and zoom are live on the stage** (2026-08-09 request; they used to be absent entirely).
   * The shipped gesture seam, unmodified: wheel zooms about the pointer, drag pans, both through
   * the same pure camera math the game screens use. Disabled only when a fixed `camera` prop pins
   * the view (tests, captures) — the listeners still suppress browser zoom either way, which is
   * exactly what a projector surface wants. Handlers read refs only, so the memo never re-attaches
   * the listeners mid-gesture.
   */
  const gestureHandlers = useMemo(
    () => ({
      onZoom: (screenPoint: { x: number; y: number }, factor: number) => {
        const rect = surfaceRef.current?.getBoundingClientRect();
        const local = { x: screenPoint.x - (rect?.left ?? 0), y: screenPoint.y - (rect?.top ?? 0) };
        setUserCamera(zoomAboutScreenPoint(cameraRef.current, local, factor, DEMO_ZOOM_BOUNDS));
      },
      onPan: (dxPx: number, dyPx: number) => {
        setUserCamera(panByScreenDelta(cameraRef.current, dxPx, dyPx));
      },
    }),
    [],
  );
  useCanvasCameraGestures(surfaceRef, gestureHandlers, camera === undefined);

  return (
    <div
      ref={surfaceRef}
      data-testid="demo-stage-surface"
      data-selected-bee-id={selectedId ?? ''}
      style={SURFACE_STYLE}
      onMouseDown={(event) => {
        // `ctrlKey` is macOS's other right-click: it fires this `mousedown` with button 0 *before*
        // the context menu. Re-picking on it clears the selection whenever the presenter points at
        // open ground, so the command that follows has no bee — "clicking does nothing" on the
        // trackpad the show is presented from.
        if (event.button !== 0 || event.ctrlKey) return;
        // Through the shipped picker, unmodified — so the ×0.72 inverse and the 44 px effective
        // target come free and cannot drift from the renderer.
        const picked = pickEntityAt(
          screenPointOf(event),
          cameraRef.current,
          [...(worldRef.current?.bees.values() ?? [])].filter(isCommandable),
          PICK_RADIUS_PX,
        );
        setSelectedId(picked);
      }}
      onContextMenu={(event) => {
        // Suppressed on the stage surface whether or not anything is selected: a browser menu
        // over the world is the one thing a projector must never show.
        event.preventDefault();
        const id = selectedRef.current;
        // A command is only ever addressed to a commandable own bee — the pick-time guard again,
        // re-checked here because a selection can go stale between the two clicks.
        if (id === null || submitCommand === undefined || !isCommandable(worldRef.current?.bees.get(id))) {
          return;
        }
        const [wx, wy] = worldPointOf(event);
        const targets = targetsRef.current;
        // Hit-target resolution (**DD5**): flower, then hive, then ground. No mode, no toolbar,
        // no modifier — the audience sees pointing.
        const patch = targets.patches.find(
          (candidate) =>
            candidate.workable &&
            Math.hypot(candidate.position[0] - wx, candidate.position[1] - wy) <= candidate.radius,
        );
        if (patch !== undefined) {
          submitCommand({ beeIds: [id], command: { command: 'collect_nectar', patchId: patch.id } });
          return;
        }
        if (
          targets.hive !== null &&
          Math.hypot(targets.hive[0] - wx, targets.hive[1] - wy) <= hiveRadiusU
        ) {
          submitCommand({ beeIds: [id], command: { command: 'drop_nectar' } });
          return;
        }
        submitCommand({ beeIds: [id], command: { command: 'move_to', target: { x: wx, y: wy } } });
      }}
    >
      {createRenderer !== undefined && (
        <div ref={hostRef} data-testid="demo-stage-canvas" style={SURFACE_STYLE} />
      )}
    </div>
  );
}
